import { db, getDoc, doc, collection, getDocs } from '../firebaseConfig';
import { fetchAndStoreMarketData } from './fetchAndStoreMarketData_v3';

export default class CustomReplayDatafeed {
  constructor({ symbol, interval, selectedInterval, startDate, endDate, userID, sessionId, startDateMillis, endDateMillis, trade }) {
    this.symbol = symbol.replace('/', '');
    this.interval = interval;
    this.selectedInterval = selectedInterval;
    this.startDate = startDate;
    this.endDate = endDate;
    this.userID = userID;
    this.sessionId = sessionId;
    this.startDateMillis = startDateMillis;
    this.endDateMillis = endDateMillis;
    this.trade = trade;
    this.isFetching = false;
    this.barsData = [];
    this.chartWidget = null;
    console.log('CustomReplayDatafeed initialized with trade:', trade, 'Symbol:', this.symbol, 'Range:', new Date(this.startDateMillis).toISOString(), 'to', new Date(this.endDateMillis).toISOString());
  }

  processMarketData(marketData, fromMillis, toMillis) {
    console.log(`Processing ${marketData.length} bars, Range: ${new Date(fromMillis).toISOString()} to ${new Date(toMillis).toISOString()}`);
    let invalidBars = 0;

    const sortedBars = marketData.sort((a, b) => (a.time - b.time));

    console.log('Raw bars (first 2, last 2):', sortedBars.slice(0, 2).concat(sortedBars.slice(-2)));

    const bars = sortedBars
      .map((bar, index) => {
        const barTime = typeof bar.time === 'number' ? (bar.time < 10000000000 ? bar.time * 1000 : bar.time) : parseInt(bar.time, 10) * 1000;
        if (index < 2 || index >= marketData.length - 2) {
          console.log(`Bar ${index}: Raw time: ${bar.time}, Processed time: ${new Date(barTime).toISOString()}`);
        }
        return {
          time: barTime,
          open: parseFloat(bar.open),
          high: parseFloat(bar.high),
          low: parseFloat(bar.low),
          close: parseFloat(bar.close),
          volume: parseFloat(bar.volume) || 0,
        };
      })
      .filter((bar, index) => {
        const isValid = !isNaN(bar.open) && !isNaN(bar.high) && !isNaN(bar.low) && !isNaN(bar.close);
        if (!isValid) {
          invalidBars++;
          console.log(`Invalid bar ${index}: ${JSON.stringify(bar)}`);
        }
        return isValid;
      });

    console.log(`Filtering summary: ${invalidBars} invalid bars`);
    console.log(`Processed bars: ${bars.length}`);
    if (bars.length > 0) {
      console.log(`First 2 bars:`, bars.slice(0, 2).map(b => ({ time: new Date(b.time).toISOString(), ...b })));
      console.log(`Last 2 bars:`, bars.slice(-2).map(b => ({ time: new Date(b.time).toISOString(), ...b })));
    } else {
      console.log('No bars after filtering');
    }

    return bars;
  }

  async fetchAllBarsFromFirestore(interval) {
    console.log(`Fetching bars for ${this.symbol} at interval ${interval}`);
    try {
      const intervalRef = doc(db, 'Users', this.userID, 'backtesting', this.sessionId, `intervals/${interval}`);
      console.log('Checking Firestore intervalRef:', intervalRef.path);
      const barsCollectionRef = collection(intervalRef, 'bars');
      const intervalDoc = await getDoc(intervalRef);
      console.log('intervalDoc exists:', intervalDoc.exists(), 'Data:', intervalDoc.data());

      let bars = [];
      if (!intervalDoc.exists() || intervalDoc.data()?.totalBars === 0) {
        console.log('No valid cached data (missing or empty), fetching from API');
        const intervalMillis = this.getIntervalInMillis(interval);
        const barsNeeded = Math.ceil((this.endDateMillis - this.startDateMillis) / intervalMillis);
        console.log(`Fetching ${barsNeeded} bars from ${new Date(this.startDateMillis).toISOString()} to ${new Date(this.endDateMillis).toISOString()}`);
        bars = await fetchAndStoreMarketData(
          this.userID,
          this.sessionId,
          this.symbol,
          interval,
          this.startDateMillis,
          this.endDateMillis,
          barsNeeded
        );
        console.log('Bars from API:', bars.length, 'First:', bars[0], 'Last:', bars[bars.length - 1]);
      } else {
        console.log('Cached data found, retrieving bars');
        const barsSnapshot = await getDocs(barsCollectionRef);
        console.log('Bars snapshot size:', barsSnapshot.size);
        barsSnapshot.forEach((doc) => {
          const docData = doc.data().data || [];
          console.log('Doc bars:', docData.length);
          bars.push(...docData);
        });
        console.log('Cached bars retrieved:', bars.length, 'First:', bars[0], 'Last:', bars[bars.length - 1]);
        if (bars.length === 0) {
          console.log('Empty cache detected, refetching from API');
          const intervalMillis = this.getIntervalInMillis(interval);
          const barsNeeded = Math.ceil((this.endDateMillis - this.startDateMillis) / intervalMillis);
          bars = await fetchAndStoreMarketData(
            this.userID,
            this.sessionId,
            this.symbol,
            interval,
            this.startDateMillis,
            this.endDateMillis,
            barsNeeded
          );
          console.log('Bars from API after empty cache:', bars.length, 'First:', bars[0], 'Last:', bars[bars.length - 1]);
        }
      }

      console.log('Raw bars fetched:', bars.length, 'First:', bars[0], 'Last:', bars[bars.length - 1]);
      bars = this.processMarketData(bars, this.startDateMillis, this.endDateMillis);
      console.log('Bars fetched and filtered:', bars.length, 'First:', bars[0], 'Last:', bars[bars.length - 1]);
      return bars;
    } catch (error) {
      console.error('Error fetching bars:', error);
      return [];
    }
  }

  getIntervalInMillis(interval) {
    const intervals = {
      '1': 60 * 1000,
      '5': 5 * 60 * 1000,
      '15': 15 * 60 * 1000,
      '1min': 60 * 1000,
      '5min': 5 * 60 * 1000,
      '15min': 15 * 60 * 1000,
    };
    return intervals[interval] || 15 * 60 * 1000;
  }

  onReady(callback) {
    setTimeout(() => {
      callback({
        supported_resolutions: ['1', '5', '15'],
        disable_resolution_rebuild: false,
        supports_marks: true,
        supports_timescale_marks: false,
        supports_time: true,
      });
    }, 0);
  }

  async resolveSymbol(symbol, onSymbolResolvedCallback, onResolveErrorCallback) {
    const normalizedSymbol = symbol.replace('/', '');
    const isStock = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'JPM', 'WMT'].includes(normalizedSymbol);
    const formattedSymbol = isStock ? normalizedSymbol : `${normalizedSymbol.slice(0, 3)}/${normalizedSymbol.slice(3)}`;
    setTimeout(() => {
      onSymbolResolvedCallback({
        name: normalizedSymbol,
        ticker: formattedSymbol,
        description: normalizedSymbol,
        type: isStock ? 'stock' : 'forex',
        session: isStock ? '0930-1600:1234567' : '24x7',
        timezone: 'Etc/UTC',
        minmov: 1,
        pricescale: isStock ? 100 : 100000,
        has_intraday: true,
        intraday_multipliers: ['1', '5', '15'],
        supported_resolutions: ['1', '5', '15'],
        volume_precision: 2,
        data_status: 'streaming',
        exchange: isStock ? 'NASDAQ' : 'Forex',
      });
    }, 0);
  }

  async getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) {
    if (this.isFetching) {
      console.log('Already fetching, returning noData');
      onHistoryCallback([], { noData: true });
      return;
    }

    this.isFetching = true;
    try {
      const bars = await this.fetchAllBarsFromFirestore(resolution);
      if (bars.length === 0) {
        console.log('No bars fetched, returning noData');
        onHistoryCallback([], { noData: true });
        onErrorCallback('No market data available for the selected timeframe');
        return;
      }

      console.log('Bars in getBars:', bars.length, 'First:', bars[0], 'Last:', bars[bars.length - 1]);
      const marks = [];
      if (this.trade) {
        const entryTime = new Date(this.trade.entryDateTime || `${this.trade.date}T${this.trade.entryTime}:00Z`).getTime();
        const exitTime = this.trade.exitDateTime || (this.trade.exitTime && `${this.trade.date}T${this.trade.exitTime}:00Z`)
          ? new Date(this.trade.exitDateTime || `${this.trade.date}T${this.trade.exitTime}:00Z`).getTime()
          : null;
        const entryCandle = bars.find(c => c.time >= entryTime) || bars[0];
        const exitCandle = exitTime ? bars.find(c => c.time >= exitTime) || bars[bars.length - 1] : null;

        if (entryCandle) {
          marks.push({
            id: `entry_${this.trade.id}`,
            time: Math.floor(entryCandle.time / 1000),
            color: 'green',
            text: `Entry: $${this.trade.entry ? parseFloat(this.trade.entry).toFixed(5) : 'N/A'}`,
            label: 'E',
            labelFontColor: 'white',
            minSize: 20,
          });
          marks.push({
            id: `sl_${this.trade.id}`,
            time: Math.floor(entryCandle.time / 1000),
            color: 'red',
            text: `SL: $${this.trade.sl ? parseFloat(this.trade.sl).toFixed(5) : 'N/A'}`,
            label: 'S',
            labelFontColor: 'white',
            minSize: 20,
          });
          marks.push({
            id: `tp_${this.trade.id}`,
            time: Math.floor(entryCandle.time / 1000),
            color: 'green',
            text: `TP: $${this.trade.tp ? parseFloat(this.trade.tp).toFixed(5) : 'N/A'}`,
            label: 'T',
            labelFontColor: 'white',
            minSize: 20,
          });
        }
        if (exitCandle && exitCandle.time !== entryCandle?.time) {
          marks.push({
            id: `exit_${this.trade.id}`,
            time: Math.floor(exitCandle.time / 1000),
            color: 'blue',
            text: `Exit: $${this.trade.exit ? parseFloat(this.trade.exit).toFixed(5) : 'N/A'}`,
            label: 'X',
            labelFontColor: 'white',
            minSize: 20,
          });
        }
        console.log('Generated marks:', marks);
      }

      this.barsData = bars;
      onHistoryCallback(bars, { noData: false, marks });
    } catch (error) {
      console.error('Error fetching bars:', error);
      onErrorCallback(`Failed to fetch market data: ${error.message}`);
    } finally {
      this.isFetching = false;
    }
  }

  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
    // No real-time updates for replay
  }

  unsubscribeBars(subscriberUID) {
    // No-op
  }
}